<script setup lang="ts">
import { UploadIcon } from '@heroicons/vue/outline';
import { defineProps, ref, toRefs, watch } from 'vue';
import { FilebaseUploader } from '~/utilities/filebase-uploader';

const uploader = new FilebaseUploader();

const props = defineProps({
  modelValue: String,
  name: String,
  profileId: String,
  accept: { type: String, default: '*' },
});
const emit = defineEmits(['update:modelValue']);

const { modelValue, name, accept, profileId } = toRefs(props);

watch(modelValue!, (value) => {
  emit('update:modelValue', value);
});

const isUploading = ref(false);
const fileInput = ref();

const selectedFile = async () => {
  const file = fileInput.value.files[0];
  if (file) {
    isUploading.value = true;
    try {
      console.info(`Uploading ${file.name}`);
      const result = await uploader.upload(profileId.value, [file]);
      console.info(`Uploaded to ${result[0].uris[0]}`);
      emit('update:modelValue', result[0].uris[0]);
    } catch (e) {
      console.error('Failed to upload file', e);
    } finally {
      isUploading.value = false;
    }
  }
  fileInput.value.value = '';
};
</script>

<template>
  <div class="flex w-full">
    <input
      :id="`${name}UrlInput`"
      type="text"
      :value="modelValue"
      :disabled="isUploading"
      :placeholder="`https://mintgarden.io/${name}.${name === 'license' ? 'pdf' : 'png'}`"
      :class="isUploading ? 'cursor-not-allowed bg-neutral-100 dark:bg-neutral-800' : ''"
      class="block w-full rounded-md border-neutral-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
      @input="(event) => emit('update:modelValue', event.target.value)"
    />
    <label
      :for="`${name}File`"
      class="ml-3 inline-flex cursor-pointer items-center rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 dark:border-neutral-600 dark:bg-black dark:text-neutral-200 dark:hover:bg-neutral-900"
    >
      <input
        :id="`${name}File`"
        ref="fileInput"
        :disabled="isUploading"
        type="file"
        class="hidden"
        :accept="accept"
        @change="selectedFile"
      />
      <svg
        v-if="isUploading"
        class="-ml-1 mr-3 h-5 w-5 animate-spin text-neutral-400 dark:text-neutral-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        ></path>
      </svg>
      <UploadIcon v-else class="-ml-2 mr-1 h-5 w-5 text-neutral-400 dark:text-neutral-500" aria-hidden="true" />
      Upload
    </label>
  </div>
</template>
